import { Link } from "react-router-dom";
import { V2, timeAgo } from "./tokens";
import { Eyebrow, Pill, HairLine, ArticleMedia } from "./V2Shared";

const metaPills = (a: any) => {
  const pills: { label: string; color: string }[] = [];
  if (a.research_label) pills.push({ label: String(a.research_label).toUpperCase(), color: V2.violet });
  if (a.read_minutes) pills.push({ label: `${a.read_minutes} MIN READ`, color: V2.cyan2 });
  return pills;
};

/* Research Studio. Desktop (47:2 y5060, Gate-2.1 §6 approved) unchanged at
   xl. Phase 3 touch (58:2 / 60:2): featured hero w/ green eyebrow + larger
   display headline; two secondary reads — side-by-side w/ center divider at
   768, stacked w/ hairline at 390 — each with a truthful colored type label
   (research_label / EXPLAINER; nothing fabricated). */
export const ResearchStudio = ({ data }: { data: any }) => {
  const research = data?.research || {};
  const featured = research.featured;
  const items = research.items || [];
  const explainer = research.explainer;
  if (!featured && !items.length) return null;
  const stack = [explainer, ...items.filter((a: any) => a?.id !== explainer?.id)].filter(Boolean).slice(0, 3);
  const railColors = [V2.violet, V2.cyan2, V2.blue2];
  const touchColors = [V2.cyan2, V2.violet, V2.green2];
  const touchLabel = (a: any) =>
    a?.id === explainer?.id ? "EXPLAINER" : String(a.research_label || "RESEARCH").toUpperCase();
  return (
    <section data-testid="v2-research-studio" className="mt-[56px]">
      <Eyebrow color={V2.cyan2} size={11}>RESEARCH &amp; DEEP DIVES</Eyebrow>
      <h2 className="nl-sg text-[28px] leading-[34px] md:text-[34px] md:leading-[40px] xl:text-[38px] xl:leading-[44px] font-medium mt-[12px]" style={{ color: V2.text }}>
        <span className="md:hidden">Evidence, data and long-form intelligence.</span>
        <span className="hidden md:inline">When the headline is not enough.</span>
      </h2>
      <p className="nl-geist text-[15px] leading-[23px] md:text-[16px] md:leading-[25px] mt-[10px] max-w-[900px]" style={{ color: V2.body }}>
        <span className="md:hidden">Fewer modules. Deeper evidence. Deliberate reading space.</span>
        <span className="hidden md:inline">The pace changes here: fewer modules, deeper evidence, annotated data and deliberate reading space.</span>
      </p>
      <HairLine className="mt-[18px] hidden xl:block" />
      <div className="grid xl:grid-cols-[1fr_468px] gap-[26px] md:gap-[32px] mt-[22px] xl:mt-[26px]">
        {featured && (
          <Link to={`/article/${featured.slug}`} data-testid="v2-research-featured"
            className="group relative block rounded-[18px] md:rounded-[22px] xl:rounded-[24px] overflow-hidden min-h-[420px]"
            style={{ border: `1px solid ${V2.border2}`, background: "linear-gradient(140deg,#0a1026 0%,#101c2a 100%)" }}>
            <div aria-hidden className="absolute -top-[70px] -right-[50px] w-[420px] h-[420px] rounded-full"
              style={{ background: "radial-gradient(circle,#5b93ff26,transparent 64%)" }} />
            <div className="relative p-[18px] md:p-[24px] xl:p-[28px] flex flex-col h-full">
              <div className="xl:hidden"><Eyebrow color={V2.green2} size={10}>FEATURED RESEARCH</Eyebrow></div>
              <div className="hidden xl:block"><Eyebrow color={V2.blue2} size={11}>FEATURED RESEARCH</Eyebrow></div>
              <ArticleMedia article={featured} className="mt-[18px] xl:mt-[20px] h-[160px] md:h-[180px] rounded-[14px] xl:rounded-[16px]" />
              <h3 className="nl-sg text-[27px] leading-[33px] md:text-[30px] md:leading-[36px] xl:text-[32px] xl:leading-[38px] font-medium mt-[18px] xl:mt-[20px] line-clamp-2 group-hover:opacity-90 transition-opacity duration-200"
                style={{ color: V2.text }}>
                {featured.title}
              </h3>
              {(featured.display_dek || featured.raw_excerpt) && (
                <p className="nl-geist text-[13px] leading-[20px] md:text-[14px] md:leading-[22px] xl:text-[15px] xl:leading-[24px] mt-[12px] line-clamp-2" style={{ color: V2.body }}>
                  {featured.display_dek || featured.raw_excerpt}
                </p>
              )}
              <div className="flex flex-wrap gap-[10px] mt-auto pt-[18px]">
                {metaPills(featured).map((p) => <Pill key={p.label} color={p.color}>{p.label}</Pill>)}
                <span className="nl-mono text-[11px] leading-[28px]" style={{ color: V2.metaMuted }}>
                  {timeAgo(featured.published_at)}
                </span>
              </div>
            </div>
          </Link>
        )}
        {stack.length > 0 && (
          <div className="min-w-0">
            <div className="hidden xl:block"><Eyebrow color={V2.violet} size={11}>THE EXPLAINER DESK</Eyebrow></div>
            <div className="relative md:grid md:grid-cols-2 md:gap-x-[40px] xl:block xl:mt-[6px]">
              <div aria-hidden className="hidden md:block xl:hidden absolute left-1/2 top-[4px] bottom-[4px] w-px"
                style={{ background: V2.border2 }} />
              {stack.map((a: any, i: number) => (
                <Link key={a.id} to={`/article/${a.slug}`} data-testid={`v2-research-secondary-${i + 1}`}
                  className={`group relative block py-[16px] md:py-0 xl:py-[20px] pl-0 xl:pl-[18px] border-[#222234] ${i === 2 ? "hidden xl:block" : ""} ${i > 0 ? "max-md:border-t xl:border-t" : ""}`}>
                  <span aria-hidden className="hidden xl:block absolute left-0 top-[24px] bottom-[24px] w-[2px] rounded"
                    style={{ background: railColors[i % railColors.length] }} />
                  <div className="xl:hidden flex items-baseline gap-[8px]">
                    <span className="nl-geist text-[10px] leading-[13px] md:text-[11px] md:leading-[14px] font-semibold uppercase tracking-[0.07em]"
                      style={{ color: touchColors[i % touchColors.length] }}>
                      {touchLabel(a)}
                    </span>
                    <span className="nl-mono text-[10px] leading-[13px] ml-auto shrink-0" style={{ color: V2.metaMuted }}>
                      {timeAgo(a.published_at)}
                    </span>
                  </div>
                  <div className="hidden xl:flex flex-wrap items-center gap-[8px]">
                    {metaPills(a).map((p) => <Pill key={p.label} color={p.color}>{p.label}</Pill>)}
                    <span className="nl-mono text-[10px] leading-[13px] ml-auto" style={{ color: V2.faint }}>
                      {timeAgo(a.published_at)}
                    </span>
                  </div>
                  <h4 className="nl-sg text-[22px] leading-[27px] md:text-[24px] md:leading-[30px] xl:text-[20px] xl:leading-[26px] font-medium mt-[10px] line-clamp-2 group-hover:opacity-90 transition-opacity duration-200"
                    style={{ color: V2.text }}>
                    {a.title}
                  </h4>
                  {(a.display_dek || a.raw_excerpt) && (
                    <p className="nl-geist text-[12px] leading-[18px] md:text-[13px] md:leading-[20px] mt-[6px] line-clamp-2" style={{ color: V2.body }}>
                      {a.display_dek || a.raw_excerpt}
                    </p>
                  )}
                  <span className="nl-geist text-[11px] leading-[14px] font-semibold tracking-[0.08em] mt-[2px] xl:mt-[10px] inline-flex items-center min-h-[44px] xl:min-h-0 group-hover:translate-x-[2px] transition-transform duration-200"
                    style={{ color: railColors[i % railColors.length] }}>
                    READ →
                  </span>
                </Link>
              ))}
            </div>
            <HairLine className="mt-[4px]" />
            <Link to="/research" data-testid="v2-research-desk-cta"
              className="nl-geist inline-flex items-center min-h-[44px] xl:min-h-0 text-[11px] leading-[14px] font-semibold tracking-[0.1em] xl:mt-[14px] hover:translate-x-[2px] transition-transform duration-200"
              style={{ color: V2.muted }}>
              VISIT THE RESEARCH DESK →
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};
